import {
  useState,
  useEffect,
  useCallback,
  Dispatch,
  SetStateAction,
  useMemo,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Checkbox,
} from "@pushpress/pushpress-ui";
import { ChevronUp, ChevronDown } from "lucide-react";
import { joinStrings } from "../../../../utils/misc";
import UserAvatar from "../../../../components/common/UserAvatar/UserAvatar";
import { MemberDataType } from "../../types";
import { Plan } from "../../../../apis/subscriptionApi";
import { getPaymentBillingTypeLabel } from "../../helpers";

type MembersTableProps = {
  membersData: MemberDataType[];
  selectedUsers: number[];
  plans?: Plan[];
  showSubAccount?: boolean;
  showPlan?: boolean;
  setSelectedUsers: Dispatch<SetStateAction<number[]>>;
};

export default function MembersTable({
  membersData,
  selectedUsers,
  plans,
  showSubAccount,
  showPlan,
  setSelectedUsers,
}: MembersTableProps) {
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const getSubAccountIds = useCallback(
    (parentEmail: string) => {
      return membersData
        .filter((user) => user.parentEmail === parentEmail)
        .map((user) => user.id);
    },
    [membersData],
  );

  const plansMap = useMemo(() => {
    return plans?.reduce(
      (acc, plan) => {
        acc[plan.planUuid] = plan;
        return acc;
      },
      {} as Record<string, Plan>,
    );
  }, [plans]);

  const toggleUserSelection = (userId: number) => {
    const user = membersData.find((u) => u.id === userId);
    if (!user) return;

    setSelectedUsers((prev) => {
      const newSelection = [...prev];
      const index = newSelection.indexOf(userId);

      if (index === -1) {
        newSelection.push(userId);
        if (!user.parentEmail) {
          newSelection.push(...getSubAccountIds(user.email));
        }
      } else {
        newSelection.splice(index, 1);
        if (!user.parentEmail) {
          getSubAccountIds(user.email).forEach((subId) => {
            const subIndex = newSelection.indexOf(subId);
            if (subIndex !== -1) {
              newSelection.splice(subIndex, 1);
            }
          });
        }
      }
      return newSelection;
    });
  };

  const toggleAllSelection = () => {
    if (selectedUsers.length === membersData.length) {
      setSelectedUsers([]);
    } else {
      const allIds = membersData.map((user) => user.id);
      setSelectedUsers(allIds);
    }
  };

  const getParentMemberName = (member: MemberDataType) => {
    if (!member.parentEmail) return "";
    const parent = membersData.find(
      (user) => user.email === member.parentEmail,
    );
    return parent ? joinStrings(parent.firstName, parent.lastName) : "";
  };

  const isAllSelected =
    membersData.length > 0 && selectedUsers.length === membersData.length;

  const sortedUsers = [...membersData].sort((a, b) => {
    if (a.firstName < b.firstName) return sortOrder === "asc" ? -1 : 1;
    if (a.firstName > b.firstName) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  useEffect(() => {
    // Select sub-accounts when parent is selected
    const newSelection = [...selectedUsers];
    membersData.forEach((user) => {
      if (!user.parentEmail && newSelection.includes(user.id)) {
        getSubAccountIds(user.email).forEach((subId) => {
          if (!newSelection.includes(subId)) {
            newSelection.push(subId);
          }
        });
      }
    });
    if (newSelection.length !== selectedUsers.length) {
      setSelectedUsers(newSelection);
    }
  }, [getSubAccountIds, membersData, selectedUsers, setSelectedUsers]);

  return (
    <div className="tw-table-auto">
      <div className="tw-border tw-rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="tw-w-10">
                <Checkbox
                  checked={isAllSelected}
                  onCheckedChange={toggleAllSelection}
                  aria-label="Select all rows"
                />
              </TableHead>
              <TableHead className="cursor-pointer" onClick={handleSort}>
                <div className="tw-flex tw-items-center tw-space-x-4">
                  <span>Name</span>
                  {sortOrder === "asc" ? (
                    <ChevronUp className="tw-h-4 tw-w-4" />
                  ) : (
                    <ChevronDown className="tw-h-4 tw-w-4" />
                  )}
                </div>
              </TableHead>
              {showSubAccount && <TableHead>Sub-account of...</TableHead>}
              {showPlan && <TableHead className="tw-w-[40%]">Plan</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedUsers.map((member) => {
              const memberPlan = showPlan && plansMap[member.planUuid];
              return (
                <TableRow
                  key={member.id}
                  className={`tw-h-15 ${member.parentEmail ? "tw-pl-6" : ""}`}
                >
                  <TableCell className="tw-align-middle">
                    <Checkbox
                      checked={selectedUsers.includes(member.id)}
                      onCheckedChange={() => toggleUserSelection(member.id)}
                      disabled={!!member.parentEmail}
                      aria-label={`Select ${member.firstName} ${member.lastName}`}
                    />
                  </TableCell>
                  <TableCell className="tw-align-middle">
                    <div className="tw-flex tw-items-center tw-space-x-4">
                      <UserAvatar size="medium" data={member} />
                      <span>
                        {joinStrings(member.firstName, member.lastName)}
                      </span>
                    </div>
                  </TableCell>
                  {showSubAccount && (
                    <TableCell>{getParentMemberName(member)}</TableCell>
                  )}
                  {showPlan && (
                    <TableCell>
                      <div>{memberPlan?.name || "--"}</div>
                      <div className="tw-text-slate-500">
                        {memberPlan &&
                          getPaymentBillingTypeLabel(member.planBillingType)}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
